import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  SplitRule,
  SplitRuleCreate,
  SplitRuleUpdate,
} from "@/api/schemas";

/** Všetky pravidlá delenia hrúbky (zoradené podľa hrúbky). */
export function useSplitRules(): UseQueryResult<DataResponse<SplitRule[]>> {
  return useQuery({
    queryKey: queryKeys.splitRules.list(),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<SplitRule[]>>("/split-rules");
      return data;
    },
    staleTime: 60_000, // pravidlá sa menia zriedka
  });
}

export function useCreateSplitRule(): UseMutationResult<
  DataResponse<SplitRule>,
  Error,
  SplitRuleCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SplitRuleCreate) => {
      const { data } = await api.post<DataResponse<SplitRule>>(
        "/split-rules",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.splitRules.all });
    },
  });
}

export function useUpdateSplitRule(): UseMutationResult<
  DataResponse<SplitRule>,
  Error,
  { id: string; data: SplitRuleUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<SplitRule>>(
        `/split-rules/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.splitRules.all });
    },
  });
}

export function useDeleteSplitRule(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/split-rules/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.splitRules.all });
    },
  });
}
